import { z } from 'zod';
import {
  compararDinheiro,
  compararPercentual,
  escalar,
  desescalar,
  somarPercentual,
  CASAS_DINHEIRO,
} from './aritmetica.ts';
import { dinheiroSchema, percentualSchema, uuidSchema } from './comum.ts';
import type { acaoSaldoTituloSchema } from './comum.ts';

/**
 * O que o título a pagar e o título a receber têm em comum.
 *
 * Este arquivo nasceu com o contas a receber (`.spec/010`, módulo Recebimento).
 * Antes dele, tudo o que está aqui morava em `contas-pagar.ts`, e a alternativa
 * — copiar para `contas-receber.ts` — foi descartada pelo motivo de sempre neste
 * projeto: duas cópias divergem na primeira correção, e a divergência aparece
 * como formulário que deixa salvar e servidor que recusa. A situação derivada do
 * saldo, o rateio de obra e a aritmética da baixa são **a mesma regra** nos dois
 * lados; o que muda entre eles é o sentido do dinheiro, e isso vive em
 * `@construtora/domain`, não aqui.
 *
 * O que **não** subiu para cá é o texto que fala com o usuário. "Quitado" e
 * "o que se deve" são do contas a pagar; "Recebido" e "o que se tem a receber"
 * são do contas a receber. Rótulo genérico nos dois lados seria pior que dois
 * rótulos escritos: ninguém lê "o valor da baixa abate o saldo do título" e
 * entende que o cliente pagou.
 */

/* -------------------------------------------------------------------------- */
/* Situação                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Situação do título — **leitura do saldo**, nunca coluna.
 *
 * Nenhuma das duas tabelas tem campo de situação, de propósito: uma coluna seria
 * um segundo lugar onde a mesma verdade poderia discordar, e o título com saldo
 * zero marcado "em aberto" é exatamente o estado que ninguém consegue explicar
 * depois. Aqui a situação é calculada, e a mesma função serve ao filtro da
 * listagem, ao rótulo da linha e ao `WHERE` do repositório.
 *
 * `QUITADO` é o código dos dois lados. No contas a receber a tela escreve
 * "Recebido" — o código é estável, o rótulo é que fala com quem lê.
 */
export const SITUACOES_TITULO = ['EM_ABERTO', 'PARCIAL', 'QUITADO'] as const;
export const situacaoTituloSchema = z.enum(SITUACOES_TITULO);
export type SituacaoTitulo = z.infer<typeof situacaoTituloSchema>;

export function situacaoDoTitulo(valor: string, valorSaldo: string): SituacaoTitulo {
  if (compararDinheiro(valorSaldo, '0') <= 0) return 'QUITADO';
  if (compararDinheiro(valorSaldo, valor) < 0) return 'PARCIAL';
  return 'EM_ABERTO';
}

/* -------------------------------------------------------------------------- */
/* Origem                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * O título aceita edição de valor, contraparte, rateio e classificação?
 *
 * **Só o digitado.** O título derivado é consequência de outro documento: mudar
 * o valor dele aqui faria a dívida — ou o crédito — discordar do documento que a
 * originou, e o desfazimento daquele documento, que apaga os títulos gerados,
 * levaria embora uma correção que ninguém mais saberia reconstruir. Para
 * corrigir o derivado, corrige-se a origem.
 *
 * A assinatura é `string`, e não o enum de um dos dois módulos: as duas listas
 * de origem são diferentes — o pagar tem nota e mão de obra, o receber não — e o
 * que a regra olha é só o código `MANUAL`, que é o mesmo nos dois. Tipar pelo
 * enum do pagar obrigaria o receber a converter, ou a criar uma união que
 * ninguém usa.
 */
export function tituloEditavel(origem: string): boolean {
  return origem === 'MANUAL';
}

/* -------------------------------------------------------------------------- */
/* Número do documento                                                        */
/* -------------------------------------------------------------------------- */

/** Teto do número do documento do título. Ver `numeroDocumentoSchema`. */
export const MAXIMO_NUMERO_DOCUMENTO = 30;

/**
 * O documento que originou o título — número da nota, do boleto, da guia.
 *
 * **Texto, e generoso em tamanho**, e as duas coisas são a mesma decisão: a guia
 * de tributo e a fatura de concessionária trazem códigos longos e com letra, e um
 * campo numérico de cinco dígitos obrigaria quem paga um DARF a digitar outra
 * coisa no lugar do que está no papel. Trinta caracteres cobrem o que se vê na
 * prática sem transformar o campo em observação.
 *
 * **Obrigatório nos dois lados**, inclusive no título lançado à mão: é por este
 * número que a conciliação com o extrato e a conferência com o fornecedor
 * acontecem, e um título sem ele é uma dívida que ninguém liga a documento
 * nenhum. Quem paga algo sem documento formal escreve o que identifica o acerto.
 *
 * No título gerado pela nota de compra ele é **o número da nota, sem a série**:
 * a série identifica o documento fiscal junto do número, mas o financeiro cobra
 * e concilia pelo número — e concatenar "4512/1" faria a busca por 4512 falhar.
 */
export const numeroDocumentoSchema = z
  .string()
  .trim()
  .min(1, 'Informe o número do documento.')
  .max(
    MAXIMO_NUMERO_DOCUMENTO,
    `O número do documento tem no máximo ${MAXIMO_NUMERO_DOCUMENTO} caracteres.`,
  );

/* -------------------------------------------------------------------------- */
/* Parcela                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Teto do parcelamento de um título.
 *
 * Trezentos e sessenta é o financiamento de trinta anos, que é o horizonte mais
 * longo que aparece na obra — e é folgado o bastante para não recusar nada
 * legítimo. O teto existe para o dedo escorregado: quem digita "1200" no total
 * está errando, e o erro que passa vira um "2/1200" que ninguém consegue
 * explicar sem abrir o documento de origem.
 */
export const MAXIMO_PARCELAS_TITULO = 360;

/**
 * A posição da parcela, e quantas o documento tem.
 *
 * **Duas colunas nos dois módulos**, e não uma só nem um texto "2/3". A posição
 * sozinha não diz se ainda vem coisa pela frente, que é o que quem confere a
 * dívida com o fornecedor — ou a cobrança com o cliente — precisa saber; e o
 * texto livre não ordena, não filtra e aceita qualquer coisa digitada.
 *
 * O padrão é `1`, e é o que faz o título lançado à mão nascer `1/1` sem que
 * quem o digita tenha de pensar no assunto. `.spec/010` não pede o campo; ele
 * entrou porque o número do documento é **o mesmo** em todas as parcelas — de
 * propósito, para a busca por ele achar todas —, e sem a parcela nada distingue
 * uma linha da outra na conferência a não ser o vencimento.
 */
export const numeroDaParcelaSchema = z.coerce
  .number()
  .int('A parcela é um número inteiro.')
  .min(1, 'A parcela começa em 1.')
  .max(MAXIMO_PARCELAS_TITULO, `O parcelamento vai até ${MAXIMO_PARCELAS_TITULO} parcelas.`)
  .prefault(1);

/**
 * A parcela cabe no total? Regra compartilhada com a tela.
 *
 * "3/2" é parcelamento que ninguém sabe ler, e o `CHECK` da tabela o recusa. A
 * função existe para o formulário recusar antes, com o erro preso ao campo — a
 * violação de constraint chega à tela sem dizer onde mostrá-la.
 */
export function parcelaCabeNoTotal(dados: { parcela: number; totalParcelas: number }): boolean {
  return dados.parcela <= dados.totalParcelas;
}

/** A mensagem, escrita uma vez: os dois módulos a mostram igual. */
export const MENSAGEM_PARCELA_MAIOR_QUE_TOTAL =
  'A parcela não pode ser maior que o total de parcelas do documento.';

/* -------------------------------------------------------------------------- */
/* Rateio de obra                                                             */
/* -------------------------------------------------------------------------- */

/** Teto de fatias por título — o mesmo do rateio da nota. */
export const MAXIMO_RATEIOS_TITULO = 50;

export const indiceRateioSchema = percentualSchema
  .refine((v) => Number(v) > 0, 'O índice de rateio deve ser maior que zero.')
  .refine((v) => Number(v) <= 100, 'O índice de rateio não passa de 100%.');

/**
 * Para qual obra vai a fatia do título — custo, no pagar; receita, no receber.
 *
 * A soma das fatias **pode ser menor que 100%**, e essa é a diferença mais
 * importante em relação ao rateio da nota. A parte não rateada é o que não
 * pertence a obra nenhuma — compra de almoxarifado central de um lado, receita
 * administrativa do outro; escalar as fatias até fechar 100% atribuiria às obras
 * um valor que não era delas, bem no número que vira resultado de obra.
 */
export const rateioDoTituloEntradaSchema = z.object({
  obraId: uuidSchema,
  /** Etapa ou bloco da obra. Opcional: nem toda obra é dividida em partes. */
  obraParteId: uuidSchema.nullable().prefault(null),
  indiceRateio: indiceRateioSchema,
});

export type RateioDoTituloEntrada = z.infer<typeof rateioDoTituloEntradaSchema>;

export const rateioDoTituloSchema = z.object({
  id: uuidSchema,
  obraId: uuidSchema,
  obraNome: z.string().nullable(),
  obraParteId: uuidSchema.nullable(),
  obraParteNome: z.string().nullable(),
  indiceRateio: percentualSchema,
});

export type RateioDoTitulo = z.infer<typeof rateioDoTituloSchema>;

/** O rateio de obra não passa de 100%. Menos é legítimo — ver acima. */
export function rateioDeObraCabe(rateios: readonly { indiceRateio: string }[]): boolean {
  if (rateios.length === 0) return true;

  return compararPercentual(somarPercentual(rateios.map((r) => r.indiceRateio)), '100') <= 0;
}

/**
 * Marca a linha repetida com o erro preso **àquela** posição do array.
 *
 * O índice único da tabela recusaria a segunda linha de qualquer forma, mas como
 * violação de constraint — que a tela recebe sem saber em qual linha do
 * formulário mostrar. Pegar aqui devolve o `path` que o formulário usa para
 * destacar o campo errado.
 */
export function marcarRepetidos(
  chaves: readonly string[],
  campo: string,
  subcampo: string,
  mensagem: string,
  ctx: z.RefinementCtx,
): void {
  const vistos = new Set<string>();

  chaves.forEach((chave, posicao) => {
    if (vistos.has(chave)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [campo, posicao, subcampo],
        message: mensagem,
      });
      return;
    }
    vistos.add(chave);
  });
}

/* -------------------------------------------------------------------------- */
/* Baixa                                                                      */
/* -------------------------------------------------------------------------- */

export const valorAcessorioSchema = dinheiroSchema
  .refine((v) => Number(v) >= 0, 'Juros, outros e desconto não podem ser negativos.')
  .prefault('0');

/**
 * Valor efetivamente movimentado no banco: `baixa + juros + outros − desconto`.
 *
 * Fica no contrato, e não no service, porque a tela mostra o líquido enquanto o
 * usuário digita e o servidor grava o que vale. Duas implementações discordam
 * num arredondamento, e o número muda ao salvar.
 *
 * `BigInt` sobre centavos, e não `Number`: a soma de quatro valores em ponto
 * flutuante erra na segunda casa, e aqui a segunda casa é o centavo que o
 * extrato bancário vai cobrar.
 *
 * A fórmula é a mesma nos dois módulos. O que muda é o **sentido**: no pagar o
 * líquido sai da conta, no receber entra. O sinal vive em `@construtora/domain`
 * (`sentidoMovimento`), e não aqui.
 */
export function valorLiquidoDaBaixa(valores: {
  valorBaixaSaldoTitulo: string;
  valorJuros: string;
  valorOutros: string;
  valorDesconto: string;
}): string {
  const total =
    escalar(valores.valorBaixaSaldoTitulo, CASAS_DINHEIRO) +
    escalar(valores.valorJuros, CASAS_DINHEIRO) +
    escalar(valores.valorOutros, CASAS_DINHEIRO) -
    escalar(valores.valorDesconto, CASAS_DINHEIRO);

  return desescalar(total, CASAS_DINHEIRO);
}

/**
 * A baixa cabe no saldo do título?
 *
 * Só faz sentido perguntar quando a ação **diminui** o saldo: `AUMENTA_SALDO` é
 * juro que se soma ao devido e `MANTEM_SALDO` não mexe em nada. A verificação
 * completa — incluindo o teto do valor original — está em `aplicarBaixaAoSaldo`,
 * em `@construtora/domain`; esta aqui é a que a tela usa para desabilitar o
 * botão antes de mandar.
 */
export function baixaCabeNoSaldo(
  saldoAtual: string,
  valorBaixa: string,
  acao: z.infer<typeof acaoSaldoTituloSchema>,
): boolean {
  if (acao !== 'DIMINUI_SALDO') return true;

  return compararDinheiro(valorBaixa, saldoAtual) <= 0;
}

/**
 * Estorno.
 *
 * A baixa **não é excluída** (`.spec/010`, "Estorno de baixa"): ela recebe data,
 * usuário e motivo, e passa a constar como estornada. O motivo é obrigatório —
 * é o único campo que explica, seis meses depois, por que o extrato tem duas
 * linhas contrárias no mesmo dia.
 */
export const estornarBaixaSchema = z.object({
  motivoEstorno: z
    .string()
    .trim()
    .min(5, 'Descreva o motivo do estorno com ao menos 5 caracteres.')
    .max(500),
});

export type EstornarBaixa = z.infer<typeof estornarBaixaSchema>;

/**
 * Rótulo da ação sobre o saldo.
 *
 * Este é neutro de propósito — "diminui o saldo do título" vale nos dois lados.
 * A **dica**, que explica o efeito em dinheiro, não é: ela mora em cada módulo,
 * porque "o que se deve" e "o que se tem a receber" não são a mesma frase.
 */
export const ROTULO_ACAO_SALDO_TITULO: Readonly<
  Record<z.infer<typeof acaoSaldoTituloSchema>, string>
> = {
  DIMINUI_SALDO: 'Diminui o saldo do título',
  AUMENTA_SALDO: 'Aumenta o saldo do título',
  MANTEM_SALDO: 'Mantém o saldo do título',
};
